import { keepPreviousData, useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { fetchAllPages } from "./fetchAllPages";
import { getActingTenantId } from "../auth/session";
import type {
  ResourceBundle,
  CreateResourceBundleResponse,
  ResourceFile,
  ResourceManifest,
  ResourceDownloadResponse,
  ListResponse,
  SingleResponse,
} from "./types";

// --- Gzip compression (design doc §4.2) ---
//
// The platform never inspects or transforms file bytes — compression is
// the uploading client's responsibility, done before the pre-signed PUT.
// ESP32 static file servers (ESPAsyncWebServer et al.) already serve a
// name.ext.gz file with Content-Encoding: gzip when name.ext is
// requested, so this needs no device-side support — just a client-side
// convention: compress, suffix the path with .gz, and checksum/size the
// compressed bytes (what actually counts against the model budget).

const COMPRESSIBLE_CONTENT_TYPES = new Set([
  "text/html",
  "text/css",
  "text/javascript",
  "text/plain",
  "application/javascript",
  "application/json",
  "image/svg+xml",
]);

const COMPRESSIBLE_EXTENSIONS = new Set(["html", "htm", "css", "js", "mjs", "json", "svg", "txt"]);

export function supportsGzipCompression(): boolean {
  return typeof CompressionStream === "function";
}

// suggestGzip recommends gzip-by-default for compressible text assets
// (inferred from the browser-reported MIME type, falling back to the
// file extension when the browser doesn't set one) and off for
// already-compressed binaries (images, fonts, certs).
export function suggestGzip(file: File): boolean {
  if (COMPRESSIBLE_CONTENT_TYPES.has(file.type)) return true;
  if (file.type) return false;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return COMPRESSIBLE_EXTENSIONS.has(ext);
}

// withGzSuffix normalises a device path to end in .gz, the convention
// ESP32 static file servers use to auto-serve a compressed variant.
export function withGzSuffix(path: string): string {
  return path.endsWith(".gz") ? path : `${path}.gz`;
}

// gzipBlob compresses a File/Blob with the browser's native
// CompressionStream — no third-party dependency needed. Throws if the
// browser doesn't support it; check supportsGzipCompression() first.
export async function gzipBlob(input: Blob): Promise<Blob> {
  const stream = input.stream().pipeThrough(new CompressionStream("gzip"));
  return new Response(stream).blob();
}

// isGzipBlob reports whether the bytes already are a gzip stream (magic
// number 1f 8b), e.g. a build output such as data/index.html.gz. Such a
// file must be uploaded as-is: gzipping it again still passes the
// checksum, but the device then serves a doubly-compressed page that
// browsers cannot read.
export async function isGzipBlob(input: Blob): Promise<boolean> {
  if (input.size < 2) return false;
  const head = new Uint8Array(await input.slice(0, 2).arrayBuffer());
  return head[0] === 0x1f && head[1] === 0x8b;
}

export type BundleSearchField = "name" | "model";

interface ListBundlesParams {
  limit?: number;
  cursor?: string;
  model?: string;
  status?: string;
  tenantId?: string;
  // Case-insensitive substring on searchField (name by default), applied
  // by the server across every page.
  search?: string;
  searchField?: BundleSearchField;
}

export function useResourceBundleList(params: ListBundlesParams = {}) {
  const actingTenantId = getActingTenantId();
  const scopeTenantId = params.tenantId ?? actingTenantId ?? undefined;
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.model) qs.set("model", params.model);
  if (params.status) qs.set("status", params.status);
  const search = params.search?.trim();
  if (search) {
    qs.set("search", search);
    qs.set("searchField", params.searchField ?? "name");
  }
  const query = qs.toString();

  return useQuery({
    queryKey: ["resources", scopeTenantId, params],
    queryFn: () => apiFetch<ListResponse<ResourceBundle>>(`/api/v1/resources${query ? `?${query}` : ""}`, {
      headers: scopeTenantId ? { "X-Acting-Tenant-ID": scopeTenantId } : undefined,
    }),
    placeholderData: keepPreviousData,
  });
}

export function useResourceBundle(id: string, tenantId?: string) {
  const actingTenantId = getActingTenantId();
  const scopeTenantId = tenantId ?? actingTenantId ?? undefined;
  return useQuery({
    queryKey: ["resources", scopeTenantId, id],
    queryFn: () => apiFetch<SingleResponse<ResourceBundle>>(`/api/v1/resources/${id}`, {
      headers: scopeTenantId ? { "X-Acting-Tenant-ID": scopeTenantId } : undefined,
    }),
    enabled: !!id,
  });
}

export function useCreateResourceBundle(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; model: string; description?: string }) =>
      apiFetch<SingleResponse<CreateResourceBundleResponse>>("/api/v1/resources", {
        method: "POST",
        headers: tenantId ? { "X-Acting-Tenant-ID": tenantId } : undefined,
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resources"] }),
  });
}

export function useUpdateResourceBundle(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { description?: string; status?: string } }) =>
      apiFetch<SingleResponse<ResourceBundle>>(`/api/v1/resources/${id}`, {
        method: "PUT",
        headers: tenantId ? { "X-Acting-Tenant-ID": tenantId } : undefined,
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resources"] }),
  });
}

export function useDeleteResourceBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/resources/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resources"] }),
  });
}

interface ListFilesParams {
  limit?: number;
  cursor?: string;
}

// useResourceFiles lists a bundle's working-set files. Called without
// limit/cursor it loads every page: the working set is bounded, and the
// publish gate ("all files active") must see all of it, not page one.
export function useResourceFiles(bundleId: string, params: ListFilesParams = {}) {
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  const query = qs.toString();
  const path = `/api/v1/resources/${bundleId}/files${query ? `?${query}` : ""}`;

  return useQuery({
    queryKey: ["resource-files", bundleId, params],
    queryFn: () =>
      !params.limit && !params.cursor ? fetchAllPages<ResourceFile>(path) : apiFetch<ListResponse<ResourceFile>>(path),
    enabled: !!bundleId,
    placeholderData: keepPreviousData,
  });
}

export function useRegisterResourceFile(bundleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { path: string; fileSize: number; contentType?: string }) =>
      apiFetch<SingleResponse<ResourceFile>>(`/api/v1/resources/${bundleId}/files`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resource-files", bundleId] });
      qc.invalidateQueries({ queryKey: ["resources"] });
    },
  });
}

export function useConfirmResourceFile(bundleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, checksum, replace }: { fileId: string; checksum?: string; replace?: boolean }) =>
      apiFetch<SingleResponse<ResourceFile>>(`/api/v1/resources/${bundleId}/files/${fileId}`, {
        method: "PUT",
        body: JSON.stringify({ checksum, replace }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resource-files", bundleId] });
      qc.invalidateQueries({ queryKey: ["resources"] });
    },
  });
}

export function useDeleteResourceFile(bundleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) =>
      apiFetch<void>(`/api/v1/resources/${bundleId}/files/${fileId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resource-files", bundleId] });
      qc.invalidateQueries({ queryKey: ["resources"] });
    },
  });
}

export function useResourceFileDownload(bundleId: string, fileId: string) {
  return useQuery({
    queryKey: ["resource-file-download", bundleId, fileId],
    queryFn: () =>
      apiFetch<SingleResponse<ResourceDownloadResponse>>(`/api/v1/resources/${bundleId}/files/${fileId}/download`),
    enabled: false,
  });
}

export function usePublishResourceBundle(bundleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<SingleResponse<ResourceManifest>>(`/api/v1/resources/${bundleId}/publish`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["resource-versions", bundleId] });
      qc.invalidateQueries({ queryKey: ["resources"] });
    },
  });
}

interface ListVersionsParams {
  limit?: number;
}

// useResourceVersions lists published versions newest first, a page at a
// time: fetchNextPage appends the next older page.
export function useResourceVersions(bundleId: string, params: ListVersionsParams = {}) {
  return useInfiniteQuery({
    queryKey: ["resource-versions", bundleId, params],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (params.limit) qs.set("limit", String(params.limit));
      if (pageParam) qs.set("cursor", pageParam);
      const query = qs.toString();
      return apiFetch<ListResponse<ResourceManifest>>(`/api/v1/resources/${bundleId}/versions${query ? `?${query}` : ""}`);
    },
    initialPageParam: "",
    getNextPageParam: (last) => (last.pagination?.hasMore ? last.pagination.cursor : undefined),
    enabled: !!bundleId,
  });
}
